import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, Loader2, X, ArrowUp, ArrowDown, Plus } from "lucide-react";
import { toast } from "sonner";
import { uploadMedia, resolveImg } from "@/lib/api";

/**
 * Manage an ordered list of product images.
 * - Upload (append) or replace a slot, reorder up/down, remove.
 * - The FIRST image is the primary/cover image.
 * - Emits a string[] of image URLs via onChange.
 */
export const MultiImageUploader = ({ value = [], onChange, label, testid }) => {
  const list = Array.isArray(value) ? value : [];
  const [busyIdx, setBusyIdx] = useState(null); // index being uploaded, or "new"
  const inputRef = useRef(null);
  const targetIdx = useRef(null);

  const setAt = (i, url) => {
    const next = [...list];
    next[i] = url;
    onChange(next);
  };
  const removeAt = (i) => onChange(list.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  const trigger = (i) => {
    targetIdx.current = i; // number to replace, or "new" to append
    inputRef.current?.click();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    const i = targetIdx.current;
    if (!file || i === null) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Image files lang po ang pwede");
      return;
    }
    setBusyIdx(i);
    try {
      const res = await uploadMedia(file);
      if (i === "new") onChange([...list, res.url]);
      else setAt(i, res.url);
      toast.success("Na-upload ang image");
    } catch (err) {
      toast.error("Hindi na-upload ang image");
    } finally {
      setBusyIdx(null);
      targetIdx.current = null;
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div data-testid={testid}>
      {label && <Label className="text-xs font-medium text-foreground">{label}</Label>}
      <p className="mt-0.5 text-[11px] text-muted-foreground">
        Ang unang larawan ang pangunahin (cover). Puwede kang mag-drag gamit ang mga arrow para ayusin ang pagkakasunod.
      </p>

      <div className="mt-2 space-y-2">
        {list.length === 0 && (
          <p className="rounded-lg border border-dashed border-border bg-background/40 p-3 text-xs text-muted-foreground">
            Wala pang larawan. I-tap ang “Mag-upload ng larawan” sa ibaba.
          </p>
        )}

        {list.map((url, i) => (
          <div
            key={i}
            className="flex items-center gap-2 rounded-lg border border-border bg-background/60 p-2"
            data-testid={testid ? `${testid}-row-${i}` : undefined}
          >
            <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-md border border-border bg-muted">
              {url ? (
                <img src={resolveImg(url)} alt={`larawan ${i + 1}`} className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-[9px] text-muted-foreground">Wala</div>
              )}
              {i === 0 && (
                <span className="absolute inset-x-0 bottom-0 bg-primary/90 py-0.5 text-center text-[8px] font-semibold uppercase text-primary-foreground">
                  Cover
                </span>
              )}
            </div>

            <Input
              className="h-9 flex-1"
              placeholder="Image URL"
              value={url || ""}
              onChange={(e) => setAt(i, e.target.value)}
              data-testid={testid ? `${testid}-url-${i}` : undefined}
            />

            <div className="flex shrink-0 items-center gap-0.5">
              <Button type="button" variant="ghost" size="icon" className="h-8 w-8" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Ilipat pataas">
                <ArrowUp className="h-4 w-4" />
              </Button>
              <Button type="button" variant="ghost" size="icon" className="h-8 w-8" disabled={i === list.length - 1} onClick={() => move(i, 1)} aria-label="Ilipat pababa">
                <ArrowDown className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8"
                disabled={busyIdx !== null}
                onClick={() => trigger(i)}
                data-testid={testid ? `${testid}-replace-${i}` : undefined}
              >
                {busyIdx === i ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-destructive"
                onClick={() => removeAt(i)}
                aria-label="Alisin ang larawan"
                data-testid={testid ? `${testid}-remove-${i}` : undefined}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="mt-2"
        disabled={busyIdx !== null}
        onClick={() => trigger("new")}
        data-testid={testid ? `${testid}-add` : undefined}
      >
        {busyIdx === "new" ? (
          <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Ina-upload...</>
        ) : (
          <><Plus className="mr-1.5 h-3.5 w-3.5" /> Mag-upload ng larawan</>
        )}
      </Button>

      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
    </div>
  );
};
